// @ts-strict-ignore
import {Component, inject, Input, OnInit, signal} from "@angular/core";
import {Translations} from "@translations";
import {ExamSession, FetchDataType, TrainingRoom, UpsertExamSessionMetaDataRequest} from "@models";
import {ExamSessionStateService} from "../../service/exam-session-state.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {SimTimeService} from "../../../../../../../core/services/sim-services/sim-time.service";
import {debounceTime, distinctUntilChanged, filter} from "rxjs/operators";
import {SimEventService, SimMaterialDialogService, NotificationService, SimSessionService} from "@services";
import {DialogData_DetailedRoomSelectionOutput} from "../../../../general-dialogs/detailed-room-selection-dialog/detailed-room-selection-dialog.component";

@UntilDestroy()
@Component({
	selector: "sim-exam-session-step1",
	templateUrl: "./exam-session-step1.component.html",
	styleUrl: "./exam-session-step1.component.scss",
	standalone: false,
})
export class ExamSessionStep1Component implements OnInit {
	// I/O
	@Input({required: true}) eventStartDate: Date;
	@Input({required: true}) eventEndDate: Date;

	// Readonly
	protected readonly T = Translations;
	protected readonly FetchDataType = FetchDataType;

	// Signal
	public floorScreenRooms = signal<TrainingRoom[]>([]);

	// Injections
	private _stateService = inject(ExamSessionStateService);
	private _sessionService = inject(SimSessionService);
	private _eventService = inject(SimEventService);
	private _notificationService = inject(NotificationService);
	private _dialogService = inject(SimMaterialDialogService);

	// Service Props
	public step1Form = this._stateService.step1Form;
	public excludeRoomIds = this._stateService.excludeRoomIds.asReadonly();

	public ngOnInit() {
		this._updateFloorScreensDisplay();
		this.step1Form.valueChanges
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(),
				filter(Boolean),
				filter(() => this.step1Form.valid),
				debounceTime(500),
			)
			.subscribe((value) => {
				// Update Exam Phases -> needed for AssessmentRecord
				this._stateService.updateExamFlowDependencies(value.examFlow);

				// Send upsert request
				const savePromise = this._sessionService.upsertExamSessionMetaData({
					sessionId: this._stateService.session.id,
					eventId: this._stateService.session.eventId,
					title: value.title,
					closing: {durationInSeconds: value.closingDurationMinutes * 60},
					confirmation: {durationInSeconds: value.confirmationDurationMinutes * 60},
					examFlowId: value.examFlow.id,
					startTime: SimTimeService.addTimeStringToDate(value.date, value.time),
					language: "", // TODO add language key to form
					floorRoomIds: value.floorScreenRooms.map((room) => room.id),
				} as UpsertExamSessionMetaDataRequest);
				this._stateService.savePromise = savePromise;
				savePromise.then((session) => this._updateSessionState(session));
			});
	}

	protected addRoom(inputRoom: TrainingRoom): void {
		this.step1Form.patchValue({floorScreenRooms: [...this.step1Form.value.floorScreenRooms, inputRoom]});
		this._updateFloorScreensDisplay();
	}

	protected removeRoom(room: TrainingRoom): void {
		this.step1Form.patchValue({floorScreenRooms: this.step1Form.value.floorScreenRooms.filter((r) => r.id !== room.id)});
		this._updateFloorScreensDisplay();
	}

	protected openRoomDetailDialog(): void {
		this._dialogService
			.openDetailedTrainingRoomSelection(
				{
					preselectedTrainingRooms: this.step1Form.value.floorScreenRooms,
					excludeRoomIds: this.excludeRoomIds(),
					singleRoom: false,
				},
				{width: "1100px", height: "825px"},
			)
			.then((data: DialogData_DetailedRoomSelectionOutput) => {
				if (data.trainingRoomSelection) {
					this.step1Form.controls.floorScreenRooms.setValue(data.trainingRoomSelection);
					this._updateFloorScreensDisplay();
				}
			});
	}

	private _updateFloorScreensDisplay(): void {
		this.floorScreenRooms.set(this.step1Form.value.floorScreenRooms);
	}

	private _updateSessionState(session: ExamSession): void {
		const existingId = this._stateService.session.id;
		this._stateService.session = session;
		if (!existingId) {
			this._eventService
				.addSessionToEvent(session.eventId, session.id)
				.catch(() => this._notificationService.notifyError("Error while adding session to event"));
		}
	}
}
